import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Star, Send, AlertCircle, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import PageTransition from "@/components/mobile/PageTransition";

const CATEGORIES = [
  { value: "feature_request", label: "Feature Request" },
  { value: "bug_report", label: "Bug Report" },
  { value: "general_feedback", label: "General Feedback" },
  { value: "navigation", label: "Navigation" },
  { value: "weather", label: "Weather" },
  { value: "learning", label: "Learning" },
  { value: "performance", label: "Performance" },
  { value: "ui_ux", label: "UI/UX" },
];

export default function FeedbackPage() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    skipper_name: "",
    skipper_email: "",
    rating: 5,
    category: "general_feedback",
    title: "",
    message: "",
    is_public: false,
  });
  const [submitted, setSubmitted] = useState(false);

  // Get current user
  useEffect(() => {
    base44.auth.me().then(setUser).catch(() => null);
  }, []);

  // Fetch public reviews
  const { data: reviews = [] } = useQuery({
    queryKey: ["public_reviews"],
    queryFn: () => base44.entities.Feedback.filter({ is_public: true }, "-created_date", 50),
  });

  // Submit feedback mutation
  const submitMutation = useMutation({
    mutationFn: (data) => base44.entities.Feedback.create(data),
    onSuccess: () => {
      setSubmitted(true);
      setFormData({
        skipper_name: "",
        skipper_email: "",
        rating: 5,
        category: "general_feedback",
        title: "",
        message: "",
        is_public: false,
      });
      setTimeout(() => setSubmitted(false), 5000);
    },
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.skipper_name && formData.skipper_email && formData.title && formData.message) {
      submitMutation.mutate(formData);
    }
  };

  const StarRating = ({ value, onChange }) => (
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          onClick={() => onChange(star)}
          className={cn(
            "text-2xl transition-colors",
            star <= value ? "text-yellow-400" : "text-muted-foreground hover:text-yellow-400"
          )}
          type="button"
        >
          ★
        </button>
      ))}
    </div>
  );

  return (
    <PageTransition>
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Share Your Feedback</h1>
            <p className="text-muted-foreground">Help us improve Marstick. Your feedback helps the maritime community.</p>
          </div>

          {/* Feedback Form */}
          <Card className="border border-primary/30">
            <CardHeader>
              <CardTitle>Submit Feedback</CardTitle>
              <CardDescription>Tell us what you think about your experience</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Your Name</label>
                  <input
                    type="text"
                    name="skipper_name"
                    value={formData.skipper_name}
                    onChange={handleInputChange}
                    placeholder="Captain's name"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Email</label>
                  <input
                    type="email"
                    name="skipper_email"
                    value={formData.skipper_email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Rating</label>
                  <StarRating value={formData.rating} onChange={(val) => setFormData(prev => ({ ...prev, rating: val }))} />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Brief title"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Your Feedback</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your experience..."
                    rows="5"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    required
                  />
                </div>

                {/* Public Review Checkbox */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleInputChange}
                    id="is_public"
                    className="cursor-pointer"
                  />
                  <label htmlFor="is_public" className="text-sm font-medium cursor-pointer">
                    Make this a public review (show on community page)
                  </label>
                </div>

                {/* Status Messages */}
                {submitted && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2 text-emerald-600">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span>Thank you! Your feedback has been received.</span>
                  </div>
                )}

                {submitMutation.isPending && (
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/30 flex items-center gap-2 text-primary">
                    <div className="animate-spin">⏳</div>
                    <span>Submitting...</span>
                  </div>
                )}

                {submitMutation.isError && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 flex items-center gap-2 text-destructive">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>Error submitting feedback. Please try again.</span>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={submitMutation.isPending}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit Feedback
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Public Reviews */}
          {reviews.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Community Reviews</h2>
              <div className="grid gap-4">
                {reviews.map(review => (
                  <Card key={review.id} className="border border-primary/20">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{review.title}</CardTitle>
                          <CardDescription>{review.skipper_name}</CardDescription>
                        </div>
                        <div className="flex gap-0.5">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm text-muted-foreground">{review.message}</p>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="bg-primary/20 text-primary px-2 py-1 rounded">
                          {CATEGORIES.find(c => c.value === review.category)?.label}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </PageTransition>
  );
}